import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

import { computed } from "vue";
import { AttributeChipProps } from "@/types";
import {
  camelcaseToDisplay,
  getEnumColor,
  getScoreColor,
  uppercaseToDisplay,
} from "@/util";
import { timStore, useTheme } from "@/hooks";
import { FlexBox, Typography } from "../content";
import { Icon } from "../icon";
import Chip from "./Chip.vue";


/**
 * Displays a generic chip that can render specific attributes.
 */
const __default__ = {
  name: "AttributeChip",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    value: {},
    format: { type: Boolean },
    artifactType: { type: Boolean },
    deltaType: { type: Boolean },
    approvalType: { type: Boolean },
    confidenceScore: { type: Boolean },
    dense: { type: Boolean },
    color: {},
    icon: {},
    removable: { type: Boolean },
    dataCy: {}
  },
  emits: ["remove"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { darkMode } = useTheme();

const enumerated = computed(() => props.approvalType || props.deltaType);

const text = computed(() => {
  if (props.confidenceScore) {
    return String(props.value).slice(0, 4);
  } else if (enumerated.value) {
    return uppercaseToDisplay(String(props.value) || "");
  } else if (props.format) {
    return camelcaseToDisplay(String(props.value) || "");
  } else if (props.artifactType) {
    return timStore.getTypeName(String(props.value));
  } else {
    return String(props.value);
  }
});

const progress = computed(() =>
  Math.min(Math.ceil(parseFloat(String(text.value)) * 100), 100)
);

const iconId = computed(() =>
  props.artifactType ? timStore.getTypeIcon(String(props.value)) : ""
);

const typeColor = computed(() =>
  props.artifactType
    ? timStore.getTypeColor(String(props.value), true)
    : "primary"
);

const iconVisible = computed(() => iconId.value || props.icon);

const displayColor = computed(() => {
  if (props.color) {
    return props.color;
  } else if (props.confidenceScore) {
    return getScoreColor(props.value);
  } else if (props.artifactType) {
    return typeColor.value;
  } else if (enumerated.value) {
    return getEnumColor(String(props.value));
  } else {
    return "";
  }
});

const iconColor = computed(() => {
  if (props.artifactType) {
    return typeColor.value;
  } else {
    return displayColor.value;
  }
});

const trackColor = computed(() => {
  switch (displayColor.value) {
    case "positive":
      return "green-3";
    case "secondary":
      return "amber-4";
    case "negative":
      return "red-3";
    default:
      return "";
  }
});

const bgClassName = computed(() =>
  darkMode.value ? "bg-background " : "bg-neutral "
);

const bdClassName = computed(() => {
  if (enumerated.value) {
    return "";
  } else if (props.artifactType) {
    return `bd-${typeColor.value} `;
  } else {
    return "bd-primary ";
  }
});

const chipClassName = computed(
  () => "q-mr-sm attribute-chip " + bgClassName.value + bdClassName.value
);

return (_ctx: any,_cache: any) => {
  const _component_q_tooltip = _resolveComponent("q-tooltip")!
  const _component_q_linear_progress = _resolveComponent("q-linear-progress")!

  return (!props.confidenceScore)
    ? (_openBlock(), _createBlock(Chip, {
        key: 0,
        class: _normalizeClass(chipClassName.value),
        outlined: enumerated.value || props.artifactType,
        color: displayColor.value,
        removable: props.removable,
        "data-cy": props.dataCy,
        dense: props.dense,
        onRemove: _cache[0] || (_cache[0] = ($event: any) => (emit('remove')))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_tooltip, {
            hidden: text.value.length < 15
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(text.value), 1)
            ]),
            _: 1
          }, 8, ["hidden"]),
          (iconVisible.value)
            ? (_openBlock(), _createBlock(_unref(Icon), {
                key: 0,
                id: iconId.value,
                variant: props.icon,
                color: iconColor.value,
                size: props.dense ? 'xs' : 'sm'
              }, null, 8, ["id", "variant", "color", "size"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(Typography), {
            ellipsis: "",
            color: "text",
            small: text.value.length >= 20,
            l: iconVisible.value ? '1' : '',
            value: text.value
          }, null, 8, ["small", "l", "value"])
        ]),
        _: 1
      }, 8, ["class", "outlined", "color", "removable", "data-cy", "dense"]))
    : (_openBlock(), _createBlock(_unref(FlexBox), {
        key: 1,
        align: "center",
        class: "attribute-bar"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_linear_progress, {
            rounded: "",
            value: progress.value / 100,
            color: displayColor.value,
            "track-color": trackColor.value,
            size: "20px"
          }, null, 8, ["value", "color", "track-color"]),
          _createVNode(_unref(Typography), {
            l: "2",
            value: progress.value + '%',
            class: "attribute-bar-text"
          }, null, 8, ["value"])
        ]),
        _: 1
      }))
}
}

})